import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { format, addMonths, parseISO } from "date-fns";
import { useLocation, Link } from "wouter";
import type { Appliance, InsertAppliance } from "@shared/schema";
import { APPLIANCE_TYPES } from "@shared/schema";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Form, FormControl, FormField, FormItem, FormLabel, FormMessage,
} from "@/components/ui/form";
import {
  HoverCard, HoverCardContent, HoverCardTrigger,
} from "@/components/ui/hover-card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { PhotoLightbox } from "@/components/message-attachments";
import { Plus, Edit2, Trash2, ExternalLink, Upload, X, Loader2, Ticket, AlertTriangle, Sparkles, AlertCircle, ImageIcon, ShieldCheck } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useMutation } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { uploadMimeType } from "@/lib/upload-mime";

// Appliances with at least this many linked tickets are flagged as replacement
// candidates. Kept as a single source of truth so the threshold is easy to tune.
export const HIGH_TICKET_THRESHOLD = 5;

// Ticket data returned by the backend alongside each appliance
export interface ApplianceTicket {
  id: number;
  title: string;
  status: string;
  createdAt: string;
}

export type ApplianceWithTickets = Appliance & { tickets: ApplianceTicket[] };

// Client-side validation schema (mirrors insertApplianceSchema but without server-only transforms)
const applianceFormSchema = z.object({
  applianceType: z.enum(APPLIANCE_TYPES).nullable().optional(),
  make: z.string().optional().default(""),
  model: z.string().optional().default(""),
  serialNumber: z.string().optional().default(""),
  dateOfPurchase: z.string().optional().default(""),
  warrantyLengthMonths: z.coerce.number().int().min(0).nullable().optional(),
  warrantyClaimLink: z.string().optional().default(""),
  purchasedFrom: z.string().optional().default(""),
  orderLink: z.string().optional().default(""),
  photos: z.array(z.string()).optional().default([]),
  extendedWarranty: z.boolean().optional().default(false),
  extendedWarrantyNumber: z.string().optional().default(""),
  extendedWarrantyExpiration: z.string().optional().default(""),
  extendedWarrantyLink: z.string().optional().default(""),
  notes: z.string().optional().default(""),
});
type ApplianceFormValues = z.infer<typeof applianceFormSchema>;

function statusColor(status: string) {
  switch (status) {
    case "completed": return "bg-green-100 text-green-800";
    case "in_progress": return "bg-blue-100 text-blue-800";
    case "cancelled": return "bg-gray-100 text-gray-500";
    default: return "bg-yellow-100 text-yellow-800";
  }
}

function statusLabel(status: string) {
  return status.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function computeWarrantyExpires(dateOfPurchase: string | null | undefined, warrantyLengthMonths: number | null | undefined): string | null {
  if (!dateOfPurchase || !warrantyLengthMonths || warrantyLengthMonths <= 0) return null;
  try {
    const parsed = parseISO(dateOfPurchase);
    if (isNaN(parsed.getTime())) return null;
    return format(addMonths(parsed, warrantyLengthMonths), "MMM d, yyyy");
  } catch {
    return null;
  }
}

interface AppliancesPanelProps {
  propertyId: number;
  appliances: ApplianceWithTickets[];
  isLoading: boolean;
  readOnly: boolean;
  applianceFormOpen: boolean;
  setApplianceFormOpen: (open: boolean) => void;
  applianceToEdit: Appliance | null;
  setApplianceToEdit: (a: Appliance | null) => void;
  applianceToDelete: Appliance | null;
  setApplianceToDelete: (a: Appliance | null) => void;
  applianceDetailId: number | null;
  setApplianceDetailId: (id: number | null) => void;
  onAdd: (data: Partial<InsertAppliance>) => void;
  onUpdate: (id: number, data: Partial<InsertAppliance>) => void;
  onDelete: (id: number) => void;
  isSaving: boolean;
  isDeleting: boolean;
}

export function AppliancesPanel({
  propertyId,
  appliances,
  isLoading,
  readOnly,
  applianceFormOpen,
  setApplianceFormOpen,
  applianceToEdit,
  setApplianceToEdit,
  applianceToDelete,
  setApplianceToDelete,
  applianceDetailId,
  setApplianceDetailId,
  onAdd,
  onUpdate,
  isSaving,
  isDeleting,
  onDelete,
}: AppliancesPanelProps) {
  const [, navigate] = useLocation();
  const detailAppliance = applianceDetailId != null
    ? appliances.find((a) => a.id === applianceDetailId) ?? null
    : null;

  // Lightbox state for the list-row Photos column. Keyed by appliance id so the
  // gallery shows that one appliance's photos.
  const [lightbox, setLightbox] = useState<{ applianceId: number; index: number } | null>(null);
  const lightboxAppliance = lightbox != null ? appliances.find((a) => a.id === lightbox.applianceId) ?? null : null;
  const lightboxPhotos = lightboxAppliance?.photos ?? [];

  function openAdd() {
    setApplianceToEdit(null);
    setApplianceFormOpen(true);
  }

  function openEdit(a: Appliance) {
    setApplianceToEdit(a);
    setApplianceFormOpen(true);
  }

  return (
    <div data-testid="panel-appliances">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-base font-semibold text-gray-900" data-testid="text-appliances-heading">Appliances</h3>
          <p className="text-xs text-gray-500 mt-0.5">Track appliances, warranties, and serial numbers for this property.</p>
        </div>
        {!readOnly && (
          <Button size="sm" onClick={openAdd} data-testid="button-add-appliance">
            <Plus className="h-4 w-4 mr-1" />
            Add Appliance
          </Button>
        )}
      </div>

      {/* List */}
      {isLoading ? (
        <div className="space-y-2" data-testid="skeleton-appliances">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : appliances.length === 0 ? (
        <div className="border rounded-lg p-10 text-center text-gray-400" data-testid="text-appliances-empty">
          <p className="text-sm">No appliances tracked yet.</p>
          {!readOnly && (
            <Button size="sm" variant="outline" className="mt-3" onClick={openAdd} data-testid="button-add-appliance-empty">
              <Plus className="h-4 w-4 mr-1" />
              Add your first appliance
            </Button>
          )}
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead data-testid="col-type">Type</TableHead>
                <TableHead data-testid="col-make">Make</TableHead>
                <TableHead data-testid="col-model">Model</TableHead>
                <TableHead data-testid="col-serial">Serial #</TableHead>
                <TableHead data-testid="col-warranty-expires">Warranty Expires</TableHead>
                <TableHead data-testid="col-photos">Photos</TableHead>
                <TableHead data-testid="col-tickets">Tickets</TableHead>
                <TableHead className="w-24" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {appliances.map((a) => {
                const warrantyExpires = computeWarrantyExpires(a.dateOfPurchase, a.warrantyLengthMonths);
                const ticketCount = a.tickets?.length ?? 0;
                return (
                  <TableRow
                    key={a.id}
                    className="cursor-pointer"
                    onClick={() => setApplianceDetailId(a.id)}
                    data-testid={`row-appliance-${a.id}`}
                  >
                    <TableCell data-testid={`text-type-${a.id}`}>{a.applianceType || <span className="text-gray-400">—</span>}</TableCell>
                    <TableCell data-testid={`text-make-${a.id}`}>{a.make || <span className="text-gray-400">—</span>}</TableCell>
                    <TableCell data-testid={`text-model-${a.id}`}>{a.model || <span className="text-gray-400">—</span>}</TableCell>
                    <TableCell data-testid={`text-serial-${a.id}`}>{a.serialNumber || <span className="text-gray-400">—</span>}</TableCell>
                    <TableCell data-testid={`text-warranty-expires-${a.id}`}>
                      {warrantyExpires ? warrantyExpires : <span className="text-gray-400">—</span>}
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()} data-testid={`cell-photos-${a.id}`}>
                      {(a.photos?.length ?? 0) === 0 ? (
                        <span className="text-gray-400">—</span>
                      ) : (
                        <button
                          type="button"
                          className="inline-flex items-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand rounded"
                          onClick={() => setLightbox({ applianceId: a.id, index: 0 })}
                          data-testid={`button-photos-${a.id}`}
                        >
                          <img
                            src={a.photos[0]}
                            alt=""
                            className="h-9 w-9 rounded object-cover border border-gray-200"
                            data-testid={`img-photo-thumb-${a.id}`}
                          />
                          {a.photos.length > 1 && (
                            <span className="text-xs font-medium text-gray-500" data-testid={`text-photo-count-${a.id}`}>
                              +{a.photos.length - 1}
                            </span>
                          )}
                        </button>
                      )}
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()} data-testid={`cell-tickets-${a.id}`}>
                      {ticketCount === 0 ? (
                        <span className="text-gray-400">—</span>
                      ) : (
                        <div className="flex items-center gap-2 flex-wrap">
                        <HoverCard openDelay={100} closeDelay={150}>
                          <HoverCardTrigger asChild>
                            <button
                              className="inline-flex items-center gap-1 text-sm font-medium text-brand hover:underline focus:outline-none"
                              data-testid={`button-tickets-${a.id}`}
                            >
                              <Ticket className="h-3.5 w-3.5" />
                              {ticketCount}
                            </button>
                          </HoverCardTrigger>
                          <HoverCardContent className="w-80 p-0" align="start">
                            <div className="px-3 py-2 border-b bg-gray-50">
                              <p className="text-xs font-semibold text-gray-700">Linked Tickets ({ticketCount})</p>
                            </div>
                            <ul className="divide-y max-h-64 overflow-y-auto">
                              {a.tickets.map((t) => (
                                <li key={t.id} className="px-3 py-2 hover:bg-gray-50">
                                  <Link
                                    href={`/app/tasks?task=${t.id}`}
                                    className="block w-full text-left"
                                    data-testid={`link-ticket-${t.id}`}
                                  >
                                    <div className="flex items-start justify-between gap-2">
                                      <span className="text-sm font-medium text-gray-900 line-clamp-2">{t.title}</span>
                                      <Badge className={`text-xs shrink-0 ${statusColor(t.status)}`} variant="outline">
                                        {statusLabel(t.status)}
                                      </Badge>
                                    </div>
                                    <p className="text-xs text-gray-400 mt-0.5">
                                      {t.createdAt ? format(new Date(t.createdAt), "MMM d, yyyy") : ""}
                                    </p>
                                  </Link>
                                </li>
                              ))}
                            </ul>
                          </HoverCardContent>
                        </HoverCard>
                        {ticketCount >= HIGH_TICKET_THRESHOLD && (
                          <HoverCard openDelay={100} closeDelay={150}>
                            <HoverCardTrigger asChild>
                              <button
                                className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400"
                                data-testid={`badge-replace-candidate-${a.id}`}
                              >
                                <AlertTriangle className="h-3 w-3" />
                                Consider replacing
                              </button>
                            </HoverCardTrigger>
                            <HoverCardContent className="w-72 text-xs text-gray-600" align="start">
                              This appliance has {ticketCount} linked maintenance tickets
                              ({HIGH_TICKET_THRESHOLD}+ flags it as a replacement candidate).
                              Repeated repairs often cost more than a replacement — review the
                              ticket history and warranty status before deciding.
                            </HoverCardContent>
                          </HoverCard>
                        )}
                        </div>
                      )}
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-1 justify-end">
                        {!readOnly && (
                          <>
                            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEdit(a)} data-testid={`button-edit-appliance-${a.id}`}>
                              <Edit2 className="h-3.5 w-3.5" />
                            </Button>
                            <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500 hover:text-red-600" onClick={() => setApplianceToDelete(a)} data-testid={`button-delete-appliance-${a.id}`}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Add/Edit Form Dialog */}
      <ApplianceFormDialog
        open={applianceFormOpen}
        appliance={applianceToEdit}
        isSaving={isSaving}
        onClose={() => {
          setApplianceFormOpen(false);
          setApplianceToEdit(null);
        }}
        onSubmit={(data) => {
          if (applianceToEdit) {
            onUpdate(applianceToEdit.id, data);
          } else {
            onAdd(data);
          }
        }}
      />

      {/* Detail Dialog */}
      {detailAppliance && (
        <ApplianceDetailDialog
          propertyId={propertyId}
          appliance={detailAppliance}
          onClose={() => setApplianceDetailId(null)}
          onEdit={() => {
            setApplianceDetailId(null);
            openEdit(detailAppliance);
          }}
          readOnly={readOnly}
        />
      )}

      {/* Photos Lightbox (list-row Photos column) */}
      {lightbox && lightboxPhotos.length > 0 && (
        <PhotoLightbox
          images={lightboxPhotos}
          index={lightbox.index}
          messageId={`appliance-${lightbox.applianceId}`}
          onIndexChange={(idx) => setLightbox((prev) => (prev ? { ...prev, index: idx } : prev))}
          onClose={() => setLightbox(null)}
        />
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={!!applianceToDelete} onOpenChange={(v) => !v && setApplianceToDelete(null)}>
        <AlertDialogContent data-testid="dialog-delete-appliance-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Appliance?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove{" "}
              <strong>{[applianceToDelete?.make, applianceToDelete?.model].filter(Boolean).join(" ") || "this appliance"}</strong>. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-delete-appliance">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              disabled={isDeleting}
              onClick={() => applianceToDelete && onDelete(applianceToDelete.id)}
              data-testid="button-confirm-delete-appliance"
            >
              {isDeleting ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Form Dialog
// ──────────────────────────────────────────────────────────────────────────────

function ApplianceFormDialog({
  open,
  appliance,
  isSaving,
  onClose,
  onSubmit,
}: {
  open: boolean;
  appliance: Appliance | null;
  isSaving: boolean;
  onClose: () => void;
  onSubmit: (data: Partial<InsertAppliance>) => void;
}) {
  const form = useForm<ApplianceFormValues>({
    resolver: zodResolver(applianceFormSchema),
    defaultValues: {
      applianceType: null,
      make: "",
      model: "",
      serialNumber: "",
      dateOfPurchase: "",
      warrantyLengthMonths: undefined,
      warrantyClaimLink: "",
      photos: [],
      extendedWarranty: false,
      extendedWarrantyNumber: "",
      extendedWarrantyExpiration: "",
      extendedWarrantyLink: "",
      notes: "",
    },
  });
  const { toast } = useToast();
  const [uploading, setUploading] = useState(false);

  // Reset form when editing an existing appliance
  useEffect(() => {
    if (open) {
      form.reset({
        applianceType: (appliance?.applianceType as typeof APPLIANCE_TYPES[number] | null | undefined) ?? null,
        make: appliance?.make ?? "",
        model: appliance?.model ?? "",
        serialNumber: appliance?.serialNumber ?? "",
        dateOfPurchase: appliance?.dateOfPurchase ?? "",
        warrantyLengthMonths: appliance?.warrantyLengthMonths ?? undefined,
        warrantyClaimLink: appliance?.warrantyClaimLink ?? "",
        purchasedFrom: appliance?.purchasedFrom ?? "",
        orderLink: appliance?.orderLink ?? "",
        photos: appliance?.photos ?? [],
        extendedWarranty: appliance?.extendedWarranty ?? false,
        extendedWarrantyNumber: appliance?.extendedWarrantyNumber ?? "",
        extendedWarrantyExpiration: appliance?.extendedWarrantyExpiration ?? "",
        extendedWarrantyLink: appliance?.extendedWarrantyLink ?? "",
        notes: appliance?.notes ?? "",
      });
    }
  }, [open, appliance]);

  const dateOfPurchase = form.watch("dateOfPurchase");
  const warrantyLengthMonths = form.watch("warrantyLengthMonths");
  const extendedWarranty = form.watch("extendedWarranty");
  const liveWarrantyExpires = computeWarrantyExpires(dateOfPurchase, warrantyLengthMonths);

  function handleSubmit(values: ApplianceFormValues) {
    const payload: Partial<InsertAppliance> = {
      applianceType: values.applianceType || null,
      make: values.make?.trim() || null,
      model: values.model?.trim() || null,
      serialNumber: values.serialNumber?.trim() || null,
      dateOfPurchase: values.dateOfPurchase?.trim() || null,
      warrantyLengthMonths: values.warrantyLengthMonths ?? null,
      warrantyClaimLink: values.warrantyClaimLink?.trim() || null,
      purchasedFrom: values.purchasedFrom?.trim() || null,
      orderLink: values.orderLink?.trim() || null,
      photos: values.photos ?? [],
      extendedWarranty: values.extendedWarranty ?? false,
      extendedWarrantyNumber: values.extendedWarranty ? (values.extendedWarrantyNumber?.trim() || null) : null,
      extendedWarrantyExpiration: values.extendedWarranty ? (values.extendedWarrantyExpiration?.trim() || null) : null,
      extendedWarrantyLink: values.extendedWarranty ? (values.extendedWarrantyLink?.trim() || null) : null,
      notes: values.notes?.trim() || null,
    };
    onSubmit(payload);
  }

  async function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      const uploaded: string[] = [];
      const skipped: string[] = [];
      for (const file of Array.from(files)) {
        // Resolve first: a .heic pick reports an EMPTY file.type, so the raw
        // startsWith("image/") check dropped it — SILENTLY, with no toast and
        // no error row, so the photo simply never appeared.
        const resolvedMime = uploadMimeType(file);
        if (!resolvedMime.startsWith("image/")) {
          skipped.push(file.name);
          continue;
        }
        const r = await fetch("/api/app/appliances/upload-url", { method: "POST", credentials: "include" });
        if (!r.ok) throw new Error("Failed to mint upload URL");
        const { uploadUrl, objectPath } = await r.json();
        const put = await fetch(uploadUrl, { method: "PUT", headers: { "Content-Type": resolvedMime }, body: file });
        if (!put.ok) throw new Error("Upload failed");
        uploaded.push(objectPath);
      }
      if (skipped.length > 0) {
        toast({
          title: skipped.length === 1 ? "Skipped 1 file" : `Skipped ${skipped.length} files`,
          description: `Only image files can be attached: ${skipped.slice(0, 3).join(", ")}${skipped.length > 3 ? "…" : ""}`,
          variant: "destructive",
        });
      }
      if (uploaded.length > 0) {
        const current = form.getValues("photos") ?? [];
        form.setValue("photos", [...current, ...uploaded], { shouldDirty: true });
      }
    } catch (err) {
      toast({ title: "Photo upload failed", description: parseErrorMessage(err, "Couldn't upload the photo. Please try again."), variant: "destructive" });
    } finally {
      setUploading(false);
    }
  }

  const photos = form.watch("photos") ?? [];

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { if (uploading) return; onClose(); } }}>
      <DialogContent
        className="max-w-lg max-h-[90vh] overflow-y-auto"
        data-testid="dialog-appliance-form"
        onInteractOutside={(e) => { if (uploading) e.preventDefault(); }}
        onEscapeKeyDown={(e) => { if (uploading) e.preventDefault(); }}
        onPointerDownOutside={(e) => { if (uploading) e.preventDefault(); }}
      >
        <DialogHeader>
          <DialogTitle>{appliance ? "Edit Appliance" : "Add Appliance"}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="applianceType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Appliance Type</FormLabel>
                  <Select
                    value={field.value ?? ""}
                    onValueChange={(v) => field.onChange(v === "" ? null : v)}
                  >
                    <FormControl>
                      <SelectTrigger data-testid="select-appliance-type">
                        <SelectValue placeholder="Select a type…" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {APPLIANCE_TYPES.map((t) => (
                        <SelectItem key={t} value={t} data-testid={`option-type-${t}`}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="grid grid-cols-2 gap-3">
              <FormField
                control={form.control}
                name="make"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Make</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g. Whirlpool" data-testid="input-appliance-make" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="model"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Model</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g. WTW5000DW" data-testid="input-appliance-model" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="serialNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Serial Number</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g. C82040020" data-testid="input-appliance-serial" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="grid grid-cols-2 gap-3">
              <FormField
                control={form.control}
                name="dateOfPurchase"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Purchase Date</FormLabel>
                    <FormControl>
                      <Input type="date" data-testid="input-appliance-purchase-date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="warrantyLengthMonths"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Warranty Length (months)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={0}
                        placeholder="e.g. 12"
                        data-testid="input-appliance-warranty-months"
                        {...field}
                        value={field.value ?? ""}
                        onChange={(e) => field.onChange(e.target.value === "" ? null : parseInt(e.target.value, 10))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            {/* Live warranty expires preview */}
            <div className="rounded-md bg-gray-50 px-3 py-2 text-sm" data-testid="text-warranty-expires-preview">
              <span className="text-gray-500">Warranty expires: </span>
              <span className="font-medium text-gray-800" data-testid="text-warranty-expires-value">
                {liveWarrantyExpires ?? <span className="text-gray-400">Enter purchase date and warranty length</span>}
              </span>
            </div>
            <FormField
              control={form.control}
              name="warrantyClaimLink"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Warranty Claim Link</FormLabel>
                  <FormControl>
                    <Input type="url" placeholder="https://…" data-testid="input-appliance-warranty-link" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {/* Extended Warranty */}
            <div className="rounded-md border px-3 py-3 space-y-3">
              <FormField
                control={form.control}
                name="extendedWarranty"
                render={({ field }) => (
                  <FormItem className="flex items-center gap-2 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={!!field.value}
                        onCheckedChange={(v) => field.onChange(v === true)}
                        data-testid="checkbox-extended-warranty"
                      />
                    </FormControl>
                    <FormLabel className="!mt-0 cursor-pointer">This appliance has an extended warranty</FormLabel>
                  </FormItem>
                )}
              />
              {extendedWarranty && (
                <div className="space-y-3 pt-1" data-testid="section-extended-warranty-fields">
                  <FormField
                    control={form.control}
                    name="extendedWarrantyNumber"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Warranty Number</FormLabel>
                        <FormControl>
                          <Input placeholder="e.g. EW-123456" data-testid="input-extended-warranty-number" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="extendedWarrantyExpiration"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Expiration Date</FormLabel>
                        <FormControl>
                          <Input type="date" data-testid="input-extended-warranty-expiration" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="extendedWarrantyLink"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Warranty Link</FormLabel>
                        <FormControl>
                          <Input type="url" placeholder="https://…" data-testid="input-extended-warranty-link" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              )}
            </div>
            <FormField
              control={form.control}
              name="purchasedFrom"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Purchased From</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g. Amazon, Home Depot, Best Buy" data-testid="input-appliance-purchased-from" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="orderLink"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Order Link</FormLabel>
                  <FormControl>
                    <Input type="url" placeholder="https://… (receipt, order page, anything)" data-testid="input-appliance-order-link" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormItem>
              <FormLabel>Photos</FormLabel>
              <div className="space-y-2">
                {photos.length > 0 && (
                  <div className="grid grid-cols-3 gap-2" data-testid="grid-appliance-photos">
                    {photos.map((url, idx) => (
                      <div key={`${url}-${idx}`} className="relative group aspect-square rounded-md overflow-hidden border bg-gray-50">
                        <img src={url} alt={`Appliance photo ${idx + 1}`} className="h-full w-full object-cover" data-testid={`img-appliance-photo-${idx}`} />
                        <button
                          type="button"
                          onClick={() => form.setValue("photos", photos.filter((_, i) => i !== idx), { shouldDirty: true })}
                          className="absolute top-1 right-1 h-6 w-6 rounded-full bg-black/60 text-white opacity-0 group-hover:opacity-100 transition flex items-center justify-center"
                          data-testid={`button-remove-photo-${idx}`}
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <label
                  className="flex items-center justify-center gap-2 border-2 border-dashed border-gray-300 rounded-md px-3 py-4 text-sm text-gray-600 cursor-pointer hover:bg-gray-50"
                  data-testid="label-upload-photos"
                >
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                  <span>{uploading ? "Uploading…" : "Click to upload photos"}</span>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    disabled={uploading}
                    onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }}
                    data-testid="input-appliance-photos"
                  />
                </label>
              </div>
            </FormItem>
            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes</FormLabel>
                  <FormControl>
                    <Textarea rows={3} placeholder="Any additional details…" data-testid="input-appliance-notes" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {uploading && (
              <p className="text-sm text-gray-500 flex items-center gap-1.5" data-testid="text-appliance-uploading-hint">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Photos are still uploading — please wait before saving or closing.
              </p>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose} disabled={uploading} data-testid="button-cancel-appliance-form">
                Cancel
              </Button>
              <Button type="submit" disabled={isSaving || uploading} data-testid="button-save-appliance">
                {isSaving ? "Saving…" : uploading ? "Uploading photos…" : appliance ? "Save Changes" : "Add Appliance"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Detail Dialog
// ──────────────────────────────────────────────────────────────────────────────

function ApplianceDetailDialog({
  propertyId,
  appliance,
  onClose,
  onEdit,
  readOnly,
}: {
  propertyId: number;
  appliance: ApplianceWithTickets;
  onClose: () => void;
  onEdit: () => void;
  readOnly: boolean;
}) {
  const [, navigate] = useLocation();
  const warrantyExpires = computeWarrantyExpires(appliance.dateOfPurchase, appliance.warrantyLengthMonths);
  const tickets = appliance.tickets ?? [];
  const detailPhotos = appliance.photos ?? [];
  const [photoIndex, setPhotoIndex] = useState<number | null>(null);

  const analysis = useMutation({
    meta: { silentSave: true },
    mutationFn: async (): Promise<{ recommendation: string }> => {
      const res = await apiRequest(
        "POST",
        `/api/app/properties/${propertyId}/appliances/${appliance.id}/replacement-analysis`,
      );
      return res.json();
    },
  });

  return (
    <Dialog
      open
      onOpenChange={(v) => { if (!v && !analysis.isPending) onClose(); }}
    >
      <DialogContent
        className="max-w-md max-h-[90vh] overflow-y-auto"
        data-testid="dialog-appliance-detail"
        onInteractOutside={(e) => { if (analysis.isPending) e.preventDefault(); }}
        onEscapeKeyDown={(e) => { if (analysis.isPending) e.preventDefault(); }}
        onPointerDownOutside={(e) => { if (analysis.isPending) e.preventDefault(); }}
      >
        <DialogHeader>
          <DialogTitle data-testid="text-appliance-detail-title">
            {[appliance.make, appliance.model].filter(Boolean).join(" ") || "Appliance"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <DetailRow label="Type" value={appliance.applianceType} testId="text-detail-type" />
          <DetailRow label="Make" value={appliance.make} testId="text-detail-make" />
          <DetailRow label="Model" value={appliance.model} testId="text-detail-model" />
          <DetailRow label="Serial Number" value={appliance.serialNumber} testId="text-detail-serial" />
          <DetailRow
            label="Purchase Date"
            value={appliance.dateOfPurchase ? format(parseISO(appliance.dateOfPurchase), "MMM d, yyyy") : null}
            testId="text-detail-purchase-date"
          />
          <DetailRow
            label="Warranty Length"
            value={appliance.warrantyLengthMonths != null ? `${appliance.warrantyLengthMonths} months` : null}
            testId="text-detail-warranty-months"
          />
          <DetailRow label="Warranty Expires" value={warrantyExpires} testId="text-detail-warranty-expires" />
          <DetailRow label="Purchased From" value={appliance.purchasedFrom} testId="text-detail-purchased-from" />
          {appliance.orderLink ? (
            <div>
              <span className="text-gray-500 font-medium">Order Link</span>
              <div className="mt-0.5">
                <a
                  href={appliance.orderLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-brand underline underline-offset-2 inline-flex items-center gap-1 break-all"
                  data-testid="link-order"
                >
                  {appliance.orderLink}
                  <ExternalLink className="h-3.5 w-3.5 flex-shrink-0" />
                </a>
              </div>
            </div>
          ) : (
            <DetailRow label="Order Link" value={null} testId="text-detail-order-link" />
          )}
          {appliance.warrantyClaimLink ? (
            <div>
              <span className="text-gray-500 font-medium">Warranty Claim Link</span>
              <div className="mt-0.5">
                <a
                  href={appliance.warrantyClaimLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-brand underline underline-offset-2 inline-flex items-center gap-1 break-all"
                  data-testid="link-warranty-claim"
                >
                  {appliance.warrantyClaimLink}
                  <ExternalLink className="h-3.5 w-3.5 flex-shrink-0" />
                </a>
              </div>
            </div>
          ) : (
            <DetailRow label="Warranty Claim Link" value={null} testId="text-detail-warranty-link" />
          )}
          {appliance.extendedWarranty && (
            <div className="rounded-md border border-brand-subtle bg-brand-muted px-3 py-2.5 space-y-2" data-testid="section-detail-extended-warranty">
              <div className="flex items-center gap-1.5 text-gray-800 font-medium">
                <ShieldCheck className="h-4 w-4 text-brand" />
                <span data-testid="text-detail-extended-warranty">Extended Warranty</span>
              </div>
              <DetailRow label="Warranty Number" value={appliance.extendedWarrantyNumber} testId="text-detail-extended-warranty-number" />
              <DetailRow
                label="Expiration"
                value={appliance.extendedWarrantyExpiration ? format(parseISO(appliance.extendedWarrantyExpiration), "MMM d, yyyy") : null}
                testId="text-detail-extended-warranty-expiration"
              />
              {appliance.extendedWarrantyLink ? (
                <div>
                  <span className="text-gray-500 font-medium">Warranty Link</span>
                  <div className="mt-0.5">
                    <a
                      href={appliance.extendedWarrantyLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-brand underline underline-offset-2 inline-flex items-center gap-1 break-all"
                      data-testid="link-extended-warranty"
                    >
                      {appliance.extendedWarrantyLink}
                      <ExternalLink className="h-3.5 w-3.5 flex-shrink-0" />
                    </a>
                  </div>
                </div>
              ) : (
                <DetailRow label="Warranty Link" value={null} testId="text-detail-extended-warranty-link" />
              )}
            </div>
          )}
          {detailPhotos.length > 0 && (
            <div>
              <span className="text-gray-500 font-medium">Photos</span>
              <div className="mt-1 grid grid-cols-3 gap-2" data-testid="grid-detail-photos">
                {detailPhotos.map((url, idx) => (
                  <button
                    key={`${url}-${idx}`}
                    type="button"
                    onClick={() => setPhotoIndex(idx)}
                    className="block aspect-square rounded-md overflow-hidden border bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
                    data-testid={`button-detail-photo-${idx}`}
                  >
                    <img src={url} alt={`Photo ${idx + 1}`} className="h-full w-full object-cover" data-testid={`img-detail-photo-${idx}`} />
                  </button>
                ))}
              </div>
            </div>
          )}
          {appliance.notes ? (
            <div>
              <span className="text-gray-500 font-medium">Notes</span>
              <p className="mt-0.5 whitespace-pre-wrap text-gray-800" data-testid="text-detail-notes">{appliance.notes}</p>
            </div>
          ) : (
            <DetailRow label="Notes" value={null} testId="text-detail-notes" />
          )}
          <div data-testid="section-detail-tickets">
            <span className="text-gray-500 font-medium">Tickets {tickets.length > 0 ? `(${tickets.length})` : ""}</span>
            {tickets.length === 0 ? (
              <p className="mt-0.5 text-gray-400" data-testid="text-detail-tickets-empty">No linked tickets</p>
            ) : (
              <ul className="mt-1 border rounded-md divide-y overflow-hidden">
                {tickets.map((t) => (
                  <li key={t.id} className="hover:bg-gray-50">
                    <button
                      type="button"
                      className="w-full text-left px-3 py-2"
                      onClick={() => { onClose(); navigate(`/app/tasks?task=${t.id}`); }}
                      data-testid={`link-detail-ticket-${t.id}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <span className="text-sm font-medium text-gray-900 line-clamp-2">{t.title}</span>
                        <Badge className={`text-xs shrink-0 ${statusColor(t.status)}`} variant="outline">
                          {statusLabel(t.status)}
                        </Badge>
                      </div>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {t.createdAt ? format(new Date(t.createdAt), "MMM d, yyyy") : ""}
                      </p>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* AI replace-vs-repair recommendation */}
          <div className="pt-2 border-t" data-testid="section-replacement-analysis">
            <div className="flex items-center justify-between gap-2">
              <span className="text-gray-500 font-medium">AI Replacement Advice</span>
              <Button
                size="sm"
                variant="outline"
                onClick={() => analysis.mutate()}
                disabled={analysis.isPending}
                data-testid="button-replacement-analysis"
              >
                {analysis.isPending ? (
                  <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Analyzing…</>
                ) : (
                  <><Sparkles className="h-3.5 w-3.5 mr-1.5" /> {analysis.data ? "Regenerate" : "Should I replace this?"}</>
                )}
              </Button>
            </div>
            {analysis.isPending && (
              <div className="mt-2 space-y-2" data-testid="skeleton-replacement-analysis">
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-3 w-5/6" />
                <Skeleton className="h-3 w-2/3" />
              </div>
            )}
            {analysis.isError && !analysis.isPending && (
              <div
                className="mt-2 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"
                data-testid="text-replacement-analysis-error"
              >
                <AlertCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                <span>
                  {analysis.error instanceof Error ? analysis.error.message : "Couldn't generate a recommendation. Please try again."}
                </span>
              </div>
            )}
            {analysis.data && !analysis.isPending && (
              <div
                className="mt-2 rounded-md border border-brand-subtle bg-brand-muted px-3 py-2.5 text-sm text-gray-800 whitespace-pre-wrap"
                data-testid="text-replacement-analysis-result"
              >
                {analysis.data.recommendation}
              </div>
            )}
            {!analysis.data && !analysis.isPending && !analysis.isError && (
              <p className="mt-1 text-xs text-gray-400">
                Get a quick AI take on whether to repair or replace, based on ticket history and warranty status.
              </p>
            )}
          </div>
        </div>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose} disabled={analysis.isPending} data-testid="button-close-appliance-detail">Close</Button>
          {!readOnly && (
            <Button onClick={onEdit} data-testid="button-edit-from-detail">
              <Edit2 className="h-4 w-4 mr-1" />
              Edit
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
      {detailPhotos.length > 0 && (
        <PhotoLightbox
          images={detailPhotos}
          index={photoIndex}
          messageId={`appliance-detail-${appliance.id}`}
          onIndexChange={setPhotoIndex}
          onClose={() => setPhotoIndex(null)}
        />
      )}
    </Dialog>
  );
}

function DetailRow({ label, value, testId }: { label: string; value: string | null | undefined; testId: string }) {
  return (
    <div>
      <span className="text-gray-500 font-medium">{label}</span>
      <span className="ml-2 text-gray-800" data-testid={testId}>{value ?? <span className="text-gray-400">—</span>}</span>
    </div>
  );
}
